import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { Link } from '@/components/atoms/Link';
import { SanityImage } from '@/components/atoms/SanityImage';
import { ArrowRight } from 'lucide-react';
import type { SanityImageSource } from '@/components/atoms/SanityImage';
import { getDocumentUrl, getDocumentTypeLabel } from '@/lib/routing';
import { getDescriptiveLinkText } from '@/lib/linkText';
import { Box } from '@/components/atoms/Box';
import { Text } from '@/components/atoms/Text';
interface PortableRelatedProps {
  value: {
    reference: {
      _type: string;
      title: string;
      slug: {
        current: string;
      };
      card?: {
        image?: SanityImageSource;
        subtitle?: string;
      };
    };
    titleOverride?: string;
    subtitleOverride?: string;
    ctaLabelOverride?: string;
  };
}

/**
 * PortableRelated Component
 *
 * Renders a 2:1 split panel interrupter referencing site content.
 * Image on the left (desktop), content on the right.
 */
const PortableRelated = t0 => {
  const $ = _c(33);
  const {
    value
  } = t0;
  const {
    reference,
    titleOverride,
    subtitleOverride,
    ctaLabelOverride
  } = value;
  if (!reference) {
    return null;
  }
  const title = titleOverride || reference.title;
  const subtitle = subtitleOverride || reference.card?.subtitle;
  let t1;
  if ($[0] !== ctaLabelOverride || $[1] !== title) {
    t1 = getDescriptiveLinkText(ctaLabelOverride, title);
    $[0] = ctaLabelOverride;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const ctaLabel = t1;
  const t2 = reference.slug?.current;
  let t3;
  if ($[3] !== reference._type || $[4] !== t2) {
    t3 = getDocumentUrl(reference._type, t2);
    $[3] = reference._type;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const href = t3;
  let t4;
  if ($[6] !== reference.card) {
    t4 = reference.card?.image ? <SanityImage image={reference.card.image} fill={true} className="object-cover transition-transform duration-700 group-hover:scale-110" /> : <Box className="absolute inset-0 bg-primary/20 flex items-center justify-center"><Text className="text-4xl opacity-20 font-bold uppercase tracking-widest -rotate-12">AgencyQ</Text></Box>;
    $[6] = reference.card;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Box className="absolute inset-0 bg-accent/20 group-hover:bg-transparent transition-colors duration-500" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <Box className="md:w-1/2 relative aspect-wide overflow-hidden">{t4}{t5}</Box>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== reference._type) {
    t7 = getDocumentTypeLabel(reference._type);
    $[11] = reference._type;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <Text className="text-2xs font-black uppercase tracking-super text-accent-foreground/70">Related {t7}</Text>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== title) {
    t9 = <Text as="p" className="text-2xl font-bold leading-tight group-hover:text-primary transition-colors">{title}</Text>;
    $[15] = title;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <Box className="space-y-2">{t8}{t9}</Box>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== subtitle) {
    t11 = subtitle && <Text as="p" className="text-sm line-clamp-2 leading-relaxed">{subtitle}</Text>;
    $[20] = subtitle;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== ctaLabel || $[24] !== href) {
    t13 = <Box className="pt-4"><Link href={href} aria-label={ctaLabel} className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-ultra-wide hover:text-primary transition-colors group/link">{ctaLabel}{t12}</Link></Box>;
    $[23] = ctaLabel;
    $[24] = href;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t13) {
    t14 = <Box className="md:w-1/2 p-8 md:p-10 flex flex-col justify-center space-y-4">{t10}{t11}{t13}</Box>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14 || $[31] !== t6) {
    t15 = <Box className="not-prose related-content my-16 bg-accent text-accent-foreground rounded-sm overflow-hidden shadow-2xl group border border-white/5"><Box className="flex flex-col md:flex-row items-stretch">{t6}{t14}</Box></Box>;
    $[30] = t14;
    $[31] = t6;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
};
export default PortableRelated;